import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { useTransferState } from '@/features/fantasy/hooks';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/**
 * The live transfer cost for the upcoming gameweek: how many changes you've made
 * vs your last locked squad, the free transfer, and the projected −4 hit. Hidden
 * until the first gameweek has locked (your opening squad is always free). A
 * Wildcard / Free Hit waives the hit.
 */
export function TransferStrip({ fantasySeasonId }: { fantasySeasonId: string | undefined }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const query = useTransferState(fantasySeasonId);
  const state = query.data;

  if (!state?.has_team || !state.has_history) return null;

  const transfers = state.transfers_made ?? 0;
  const hit = state.projected_hit ?? 0;
  const chipFree = state.active_chip === 'wildcard' || state.active_chip === 'free_hit';

  return (
    <Card className="mx-4 mb-4 flex-row items-center gap-3 px-4 py-3.5">
      <View className="h-9 w-9 items-center justify-center rounded-full bg-inset">
        <Ionicons name="swap-horizontal" size={18} color={palette.voltBright} />
      </View>
      <View className="flex-1">
        <AppText variant="caption" weight="bold" className="text-ink">
          {t('fantasy.transfers.title')}
        </AppText>
        <AppText variant="micro" className="text-muted">
          {t('fantasy.transfers.made', { count: transfers })}
        </AppText>
      </View>
      {chipFree ? (
        <View className="rounded-full border border-volt-line bg-volt-glass px-2.5 py-1">
          <AppText variant="micro" weight="bold" className="text-volt-bright">
            {t('fantasy.transfers.chipActive')}
          </AppText>
        </View>
      ) : hit > 0 ? (
        <View className="items-end">
          <AppText variant="heading" weight="bold" tabular className="text-loss">
            −{formatNumber(hit, numeralOptions)}
          </AppText>
          <AppText variant="micro" className="text-muted">
            {t('common.pts')}
          </AppText>
        </View>
      ) : (
        <View className="rounded-full bg-surface-2 px-2.5 py-1">
          <AppText variant="micro" weight="bold" className="text-soft">
            {t('fantasy.transfers.free')}
          </AppText>
        </View>
      )}
    </Card>
  );
}
